import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Phone, 
  MapPin, 
  Clock, 
  Users, 
  Droplets, 
  Award, 
  CheckCircle2, 
  Milk, 
  Leaf, 
  Heart, 
  Shield, 
  Truck,
  Menu,
  X,
  ChevronRight,
  Star,
  Calendar,
  ArrowRight,
  Instagram,
  Linkedin
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";

// Asset URLs - User uploaded images ONLY
const ASSETS = {
  heroDeliveryPerson: "https://customer-assets.emergentagent.com/job_farm-to-home-11/artifacts/18tlloch_WhatsApp%20Image%202025-12-08%20at%207.34.47%20PM.jpeg",
  ourStoryImage: "https://customer-assets.emergentagent.com/job_farm-to-home-11/artifacts/6p6ysxfb_knop%20font%20%20%286x2.5%29.jpg.jpeg",
  brandLogo: "https://customer-assets.emergentagent.com/job_farm-to-home-11/artifacts/bcv80edb_doorstep%20logo.jpg",
  productA1Milk: "https://customer-assets.emergentagent.com/job_826f8b42-3645-4274-bff1-c7f64961b9fe/artifacts/ktsqppif_WhatsApp%20Image%202026-05-05%20at%2010.04.31%20AM.jpeg",
  productA2Milk: "https://customer-assets.emergentagent.com/job_826f8b42-3645-4274-bff1-c7f64961b9fe/artifacts/q4ty1tml_WhatsApp%20Image%202026-05-05%20at%2010.04.31%20AM%20%281%29.jpeg",
  productPaneer: "https://customer-assets.emergentagent.com/job_farm-to-home-11/artifacts/bgiwua2v_ChatGPT%20Image%20Jan%2030%2C%202026%2C%2006_16_32%20PM.png"
};

const WHATSAPP_NUMBER = "916261610040";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20Doorstep%20Doodhwala!%20I%20would%20like%20to%20know%20more%20about%20your%20fresh%20milk%20delivery.`;

// Social Media Links
const SOCIAL_LINKS = {
  instagram: {
    url: "https://www.instagram.com/doorstepdoodhwala?igsh=MWljbXZmaGZocnoxaQ==",
    username: "doorstepdoodhwala"
  },
  linkedin: {
    url: "https://www.linkedin.com/company/doorstepdoodhwala/",
    name: "Doorstep Doodhwala"
  }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About Us" },
    { href: "#products", label: "Our Milk" },
    { href: "#blog", label: "Blogs" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-md shadow-md" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo - Premium size, blended with header */}
          <a href="#home" className="flex items-center" data-testid="logo">
            <img 
              src={ASSETS.brandLogo} 
              alt="Doorstep Doodhwala" 
              className="h-12 md:h-14 w-auto object-contain rounded-md"
              style={{ 
                mixBlendMode: 'multiply'
              }}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a 
                key={link.href} 
                href={link.href} 
                className="nav-link font-body text-base"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-2 border-l border-[#E6E0D4] pl-4">
              <a 
                href={SOCIAL_LINKS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
                data-testid="nav-instagram-link"
                title="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4 text-white" />
              </a>
              <a 
                href={SOCIAL_LINKS.linkedin.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#0077B5] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
                data-testid="nav-linkedin-link"
                title="Follow us on LinkedIn"
              >
                <Linkedin className="w-4 h-4 text-white" />
              </a>
            </div>

            <a 
              href={WHATSAPP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-btn"
            >
              <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-6">
                <Phone className="w-4 h-4 mr-2" />
                Contact on WhatsApp
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white rounded-lg shadow-lg p-4 mt-2 animate-fade-in-up" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a 
                key={link.href} 
                href={link.href} 
                className="block py-3 px-4 text-lg font-body hover:bg-[#F3F0E9] rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            
            {/* Mobile Social Links */}
            <div className="flex items-center gap-3 py-3 px-4 border-t border-[#E6E0D4] mt-2">
              <span className="font-body text-sm text-[#6B6659]">Follow Us:</span>
              <a 
                href={SOCIAL_LINKS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] rounded-lg flex items-center justify-center"
              >
                <Instagram className="w-4 h-4 text-white" />
              </a>
              <a 
                href={SOCIAL_LINKS.linkedin.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#0077B5] rounded-lg flex items-center justify-center"
              >
                <Linkedin className="w-4 h-4 text-white" />
              </a>
            </div>

            <a 
              href={WHATSAPP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block mt-2"
            >
              <Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full">
                <Phone className="w-4 h-4 mr-2" />
                Contact on WhatsApp
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      className="hero-section min-h-screen pt-20 flex items-center"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="relative z-10 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-[#2E5C38]/10 text-[#2E5C38] px-4 py-2 rounded-full mb-6 animate-fade-in-up">
              <Award className="w-4 h-4" />
              <span className="font-body text-sm font-medium">5+ Years of Trusted Service</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Fresh Pure Milk Direct to Your Doorstep
            </h1>
            
            <p className="font-body text-lg md:text-xl text-[#6B6659] mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
              We deliver fresh, unadulterated milk directly from our farm to your home, every single day — with honesty, care, and purity.
            </p>

            <p className="font-heading text-2xl text-[#E89F28] font-semibold mb-8 animate-fade-in-up animation-delay-200">
              Purity ki Surety
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8 animate-fade-in-up animation-delay-300">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <CheckCircle2 className="w-5 h-5 text-[#2E5C38]" />
                <span className="font-body text-sm">No Adulteration</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Shield className="w-5 h-5 text-[#2E5C38]" />
                <span className="font-body text-sm">No Chemicals</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Leaf className="w-5 h-5 text-[#2E5C38]" />
                <span className="font-body text-sm">100% Natural</span>
              </div>
            </div>
            
            {/* CTA Button */}
            <a 
              href={WHATSAPP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block animate-fade-in-up animation-delay-400"
              data-testid="hero-whatsapp-cta"
            >
              <button className="whatsapp-btn">
                <Phone className="w-5 h-5" />
                Contact on WhatsApp
              </button>
            </a>
          </div>
          
          {/* Hero Image */}
          <div className="relative z-10 flex justify-center lg:justify-end animate-fade-in-up animation-delay-200">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E89F28]/20 rounded-3xl blur-2xl"></div>
              <img 
                src={ASSETS.heroDeliveryPerson} 
                alt="Doorstep Doodhwala - Fresh Milk Delivery"
                className="relative rounded-3xl shadow-2xl max-w-full h-auto max-h-[500px] object-contain"
                data-testid="hero-image"
              />
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2E5C38]/10 rounded-full flex items-center justify-center">
                    <Truck className="w-6 h-6 text-[#2E5C38]" />
                  </div>
                  <div>
                    <p className="font-body text-sm text-[#6B6659]">Daily Delivery</p>
                    <p className="font-body font-semibold text-[#1A1A1A]">Morning & Evening</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Stats Section
const TrustStatsSection = () => {
  const stats = [
    { icon: Users, value: "5000+", label: "Happy Customers", color: "#2E5C38" },
    { icon: Droplets, value: "4,50,000+", label: "Litres Delivered", color: "#E89F28" },
    { icon: Award, value: "5+", label: "Years of Service", color: "#8B5E3C" },
    { icon: CheckCircle2, value: "100%", label: "Pure & Natural", color: "#2E5C38" }
  ];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="trust-stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="trust-stat-card"
              data-testid={`trust-stat-${index}`}
            >
              <div 
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <stat.icon className="w-8 h-8" style={{ color: stat.color }} />
              </div>
              <p className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-2">
                {stat.value}
              </p>
              <p className="font-body text-sm md:text-base text-[#6B6659]">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
        
        {/* Trust Statement */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-wrap justify-center gap-4 md:gap-8">
            {["No Adulteration", "No Chemicals", "No Preservatives", "Farm to Home"].map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#2E5C38]" />
                <span className="font-body text-sm md:text-base text-[#1A1A1A]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-16 md:py-24 bg-[#FDFBF7]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 section-heading">
            Our Story
          </h2>
          <p className="font-body text-base md:text-lg text-[#6B6659] max-w-2xl mx-auto mt-6">
            A journey that began with a simple purpose — to bring pure, honest milk to every family.
          </p>
        </div>

        {/* Story Content - English Only */}
        <div className="max-w-4xl mx-auto space-y-6 mb-12">
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
            <h3 className="font-heading text-2xl font-semibold text-[#2E5C38] mb-4">
              The Beginning
            </h3>
            <p className="font-body text-[#6B6659] leading-relaxed">
              Doorstep Doodhwala was started with a simple but powerful purpose — to remove adulteration from daily milk and give families access to pure, healthy milk. What began as a small initiative in Village Chandkhuri has grown into a trusted name across the region.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
            <h3 className="font-heading text-2xl font-semibold text-[#8B5E3C] mb-4">
              Our Commitment
            </h3>
            <p className="font-body text-[#6B6659] leading-relaxed">
              For over 5 years, we have been serving families with milk that is 100% natural, unadulterated, and fresh. We personally handle and manage our farm, and also work with trusted local dairy farmers who share our values of purity and honesty.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
            <h3 className="font-heading text-2xl font-semibold text-[#E89F28] mb-4">
              Our Mission
            </h3>
            <p className="font-body text-[#6B6659] leading-relaxed">
              Every step we take focuses on purity, hygiene, and honesty. Milk is delivered fresh every day to homes because we believe in better health, stronger families, and removing milk adulteration from society. This isn't just a business — it's our responsibility.
            </p>
          </div>

          {/* Location Info */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <div className="flex items-center gap-3 bg-[#2E5C38]/5 px-6 py-4 rounded-lg">
              <MapPin className="w-5 h-5 text-[#2E5C38]" />
              <div>
                <p className="font-body text-xs text-[#6B6659]">Farm Location</p>
                <p className="font-body font-medium text-[#1A1A1A]">Village Chandkhuri</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#8B5E3C]/5 px-6 py-4 rounded-lg">
              <MapPin className="w-5 h-5 text-[#8B5E3C]" />
              <div>
                <p className="font-body text-xs text-[#6B6659]">Office Address</p>
                <p className="font-body font-medium text-[#1A1A1A]">Housing Board Colony, Nardaha</p>
              </div>
            </div>
          </div>
        </div>

        {/* Large Image at Bottom - Full Width */}
        <div className="mt-12">
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src={ASSETS.ourStoryImage}
              alt="Doorstep Doodhwala - Our Story"
              className="w-full h-auto object-cover"
              data-testid="about-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Products Section with Clickable Cards
const ProductsSection = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const products = [
    {
      name: "A1 Cow Milk",
      image: ASSETS.productA1Milk,
      shortDesc: "Fresh A1 cow milk rich in essential nutrients. Perfect for daily consumption for the whole family.",
      features: ["Rich in Calcium", "High Protein", "Natural Taste", "Daily Fresh"],
      fullContent: {
        title: "A1 Cow Milk",
        description: "Our A1 Cow Milk is sourced from healthy, well-maintained cows raised under hygienic and responsible conditions. We focus on milk quality, cleanliness, and freshness, ensuring a safe and reliable option for daily family consumption.",
        highlights: [
          "Fresh milk collected daily",
          "Clean and hygienic milking process",
          "Naturally rich taste and smooth consistency",
          "Suitable for children, adults, and elders",
          "Regular quality checks"
        ],
        purity: ["No adulteration", "No chemicals", "No preservatives"],
        promise: "Good milk is defined by how it is produced and handled, not by confusing labels. Our A1 milk is pure, fresh, and responsibly delivered.",
        delivery: ["Available for daily fresh delivery", "Morning & Evening delivery", "Farm to home"]
      }
    },
    {
      name: "A2 Desi Cow Milk",
      image: ASSETS.productA2Milk,
      shortDesc: "Premium A2 milk from indigenous Desi cows. Known for its easy digestibility and traditional goodness.",
      features: ["Easy to Digest", "Traditional Breed", "Nutrient Dense", "Ayurvedic Benefits"],
      fullContent: {
        title: "A2 Desi Cow Milk",
        description: "Our A2 Desi Cow Milk comes from indigenous Indian cows, traditionally raised with care and proper nutrition.",
        highlights: [
          "Naturally contains A2 protein",
          "Easy to digest for many people",
          "Traditional dairy practices",
          "Hygienic handling"
        ],
        purity: ["No adulteration", "No chemicals", "No preservatives"],
        idealFor: ["Families preferring traditional milk", "Children and elders", "Health-conscious households"],
        delivery: ["Available for daily fresh delivery", "Morning & Evening delivery", "Direct farm to doorstep"]
      }
    },
    {
      name: "Fresh Paneer",
      image: ASSETS.productPaneer,
      shortDesc: "Homemade fresh paneer prepared from our pure milk. Soft, fresh, and perfect for your kitchen.",
      features: ["Soft & Fresh", "No Preservatives", "Rich Texture", "Made from Pure Milk"],
      fullContent: {
        title: "Fresh Paneer",
        description: "Our paneer is 100% pure, milawat-free, and freshly prepared using our own high-quality milk.",
        highlights: [
          "Made only from pure fresh milk",
          "No starch",
          "No synthetic agents",
          "No preservatives",
          "Soft texture and natural taste"
        ],
        freshness: ["Prepared fresh", "Delivered within the same day", "Maximum freshness and nutrition"],
        perfectFor: ["Home cooking", "Children's meals", "Daily kitchen use"],
        delivery: ["Same-day delivery", "Farm-made, home-delivered"]
      }
    }
  ];

  return (
    <section id="products" className="py-16 md:py-24 bg-white" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 section-heading">
            Our Products
          </h2>
          <p className="font-body text-lg text-[#6B6659] max-w-2xl mx-auto mt-6">
            Pure dairy products straight from our farm to your table. Tap to learn more.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <Card 
              key={index} 
              className="product-card border-0 cursor-pointer group"
              onClick={() => setSelectedProduct(product)}
              data-testid={`product-card-${index}`}
            >
              <div className="aspect-square overflow-hidden bg-[#F3F0E9]">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-3">
                  {product.name}
                </h3>
                <p className="font-body text-[#6B6659] mb-4">
                  {product.shortDesc}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.features.map((feature, idx) => (
                    <span 
                      key={idx}
                      className="bg-[#2E5C38]/10 text-[#2E5C38] text-xs px-3 py-1 rounded-full font-body"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="pt-4 border-t border-[#E6E0D4] flex items-center justify-between">
                  <p className="font-body text-sm text-[#E89F28] font-medium flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Available for daily delivery
                  </p>
                  <ChevronRight className="w-5 h-5 text-[#2E5C38] group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a 
            href={WHATSAPP_URL} 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="products-whatsapp-cta"
          >
            <button className="whatsapp-btn">
              <Phone className="w-5 h-5" />
              Contact on WhatsApp
            </button>
          </a>
        </div>
      </div>

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7]">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl md:text-3xl text-[#2E5C38]">
                  {selectedProduct.fullContent.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                <p className="font-body text-[#6B6659] leading-relaxed">
                  {selectedProduct.fullContent.description}
                </p>

                {/* Key Highlights */}
                <div>
                  <h4 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-3">Key Highlights</h4>
                  <ul className="space-y-2">
                    {selectedProduct.fullContent.highlights.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                        <CheckCircle2 className="w-5 h-5 text-[#2E5C38] flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Purity */}
                {selectedProduct.fullContent.purity && (
                  <div className="bg-[#2E5C38]/5 rounded-xl p-4">
                    <div className="flex flex-wrap gap-3">
                      {selectedProduct.fullContent.purity.map((item, idx) => (
                        <span key={idx} className="flex items-center gap-2 bg-white px-3 py-2 rounded-full font-body text-sm text-[#2E5C38]">
                          <Shield className="w-4 h-4" />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ideal For (A2 Milk) */}
                {selectedProduct.fullContent.idealFor && (
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-3">Ideal For</h4>
                    <ul className="space-y-2">
                      {selectedProduct.fullContent.idealFor.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                          <Heart className="w-5 h-5 text-[#E89F28] flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Freshness (Paneer) */}
                {selectedProduct.fullContent.freshness && (
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-3">Freshness Guarantee</h4>
                    <ul className="space-y-2">
                      {selectedProduct.fullContent.freshness.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                          <CheckCircle2 className="w-5 h-5 text-[#2E5C38] flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Perfect For (Paneer) */}
                {selectedProduct.fullContent.perfectFor && (
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-3">Perfect For</h4>
                    <ul className="space-y-2">
                      {selectedProduct.fullContent.perfectFor.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                          <Heart className="w-5 h-5 text-[#E89F28] flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Promise (A1 Milk) */}
                {selectedProduct.fullContent.promise && (
                  <div className="bg-[#E89F28]/10 rounded-xl p-4">
                    <h4 className="font-heading text-lg font-semibold text-[#8B5E3C] mb-2">Our Promise</h4>
                    <p className="font-body text-[#6B6659]">{selectedProduct.fullContent.promise}</p>
                  </div>
                )}

                {/* Delivery */}
                <div className="bg-[#2E5C38] rounded-xl p-4 text-white">
                  <h4 className="font-heading text-lg font-semibold mb-3">Delivery</h4>
                  <ul className="space-y-2">
                    {selectedProduct.fullContent.delivery.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 font-body">
                        <Truck className="w-4 h-4 text-[#E89F28]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* WhatsApp CTA */}
                <div className="text-center pt-4">
                  <a 
                    href={WHATSAPP_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <button className="whatsapp-btn">
                      <Phone className="w-5 h-5" />
                      Contact on WhatsApp
                    </button>
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Quality Process Section
const QualityProcessSection = () => {
  const steps = [
    {
      icon: Milk,
      title: "Farm Fresh",
      description: "Our cows are raised on our own farm in Village Chandkhuri with natural feed and proper care."
    },
    {
      icon: CheckCircle2,
      title: "Quality Check",
      description: "Every batch is carefully checked for purity. No chemicals, no preservatives, no adulteration."
    },
    {
      icon: Shield,
      title: "Hygienic Process",
      description: "Milk is handled with utmost hygiene from milking to packaging, ensuring safety and freshness."
    },
    {
      icon: Truck,
      title: "Doorstep Delivery",
      description: "Fresh milk delivered to your doorstep every morning and evening, maintaining cold chain integrity."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-[#2E5C38]" data-testid="quality-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Our Quality Process
          </h2>
          <p className="font-body text-lg text-white/80 max-w-2xl mx-auto">
            From our farm to your home — every step is handled with care and honesty.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div 
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition-colors"
              data-testid={`quality-step-${index}`}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <step.icon className="w-8 h-8 text-white" />
              </div>
              <div className="bg-[#E89F28] text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4">
                {index + 1}
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {step.title}
              </h3>
              <p className="font-body text-white/80 text-sm">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Delivery Info */}
        <div className="mt-12 bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex items-center gap-4">
              <Clock className="w-8 h-8 text-[#E89F28]" />
              <div>
                <p className="font-body text-white/60 text-sm">Fresh Every Day</p>
                <p className="font-heading text-xl text-white font-semibold">Morning Delivery</p>
              </div>
            </div>
            <Separator orientation="vertical" className="h-12 bg-white/20 hidden md:block" />
            <div className="flex items-center gap-4">
              <Clock className="w-8 h-8 text-[#E89F28]" />
              <div>
                <p className="font-body text-white/60 text-sm">Fresh Every Day</p>
                <p className="font-heading text-xl text-white font-semibold">Evening Delivery</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section (Text Only - English Only with Auto-Sliding)
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Aman Agrawal",
      location: "Swarnbhoomi",
      text: "The milk quality is consistently pure and fresh. We trust Doorstep Doodhwala completely for our family's daily needs."
    },
    {
      name: "Deepak Goel",
      location: "VIP Estate",
      text: "Very hygienic delivery and always on time. It truly feels like farm-fresh milk every day."
    },
    {
      name: "Nitin Bansal",
      location: "Sapphire Green",
      text: "No chemicals, no adulteration. That's why we chose Doorstep Doodhwala for our children."
    },
    {
      name: "Pankaj Jain",
      location: "Shankar Nagar",
      text: "Reliable service and honest people. Once you start, you don't feel like switching."
    },
    {
      name: "Sachin Garg",
      location: "Daldal Seoni",
      text: "Fresh milk delivered on time morning and evening. Very reliable service for the whole family."
    },
    {
      name: "Harsh Agrawal",
      location: "Swarnbhoomi",
      text: "My children love the taste of this pure milk. Thank you Doorstep Doodhwala for the quality!"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex >= testimonials.length - 3 ? 0 : prevIndex + 1
      );
    }, 3500);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <section className="py-16 md:py-24 bg-[#F3F0E9] overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 section-heading">
            What Our Customers Say
          </h2>
        </div>

        {/* Auto-sliding testimonials - 3 at a time */}
        <div className="relative overflow-hidden">
          <div 
            className="flex transition-transform duration-700 ease-in-out gap-6"
            style={{
              transform: `translateX(-${currentIndex * (100 / 3 + 2)}%)`
            }}
          >
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-full md:w-[calc(33.333%-16px)]"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#E89F28] text-[#E89F28]" />
                  ))}
                </div>
                <p className="font-body text-[#1A1A1A] mb-4 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-[#E6E0D4] pt-4">
                  <p className="font-body font-semibold text-[#2E5C38]">{testimonial.name}</p>
                  <p className="font-body text-sm text-[#6B6659]">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {[...Array(testimonials.length - 2)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex === index ? 'bg-[#2E5C38] w-6' : 'bg-[#2E5C38]/30'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Blog Section with Clickable Cards
const BlogSection = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);

  const blogPosts = [
    {
      title: "Why Pure Milk Matters for Your Family",
      category: "Health",
      icon: Heart,
      fullContent: {
        title: "Why Pure Milk Matters for Your Family",
        paragraphs: [
          "Milk is a daily staple in most households. Pure milk supports:",
        ],
        benefits: [
          "Better digestion",
          "Stronger immunity",
          "Healthy growth in children",
          "Daily nutrition for elders"
        ],
        conclusion: [
          "Adulterated milk may contain harmful substances that affect long-term health.",
          "Choosing pure, farm-fresh milk helps protect your family's well-being and builds healthier habits."
        ]
      }
    },
    {
      title: "A1 vs A2 Milk: What's the Difference?",
      category: "Education",
      icon: Leaf,
      fullContent: {
        title: "A1 vs A2 Milk: What's the Difference?",
        paragraphs: [
          "Milk proteins are commonly classified as A1 or A2.",
        ],
        facts: [
          "A2 milk is naturally found in indigenous Indian cows",
          "A1 milk is commonly consumed and safe when produced hygienically"
        ],
        keyPoint: "The real difference lies in quality, freshness, and handling. Clean sourcing, no adulteration, and fresh delivery matter more than labels.",
        tips: [
          "Fresh",
          "Pure",
          "Responsibly sourced",
          "Delivered hygienically"
        ],
        tipsTitle: "Always choose milk that is:"
      }
    },
    {
      title: "The Hidden Dangers of Adulterated Milk",
      category: "Awareness",
      icon: Shield,
      fullContent: {
        title: "The Hidden Dangers of Adulterated Milk",
        paragraphs: [
          "Milk adulteration can include:"
        ],
        dangers: [
          "Water dilution",
          "Synthetic chemicals",
          "Artificial whiteners"
        ],
        impact: "These practices may impact digestion, immunity, and overall health over time.",
        solution: "Choosing a trusted local dairy that follows honest practices ensures:",
        benefits: [
          "Better health",
          "Peace of mind",
          "Safe daily consumption"
        ]
      }
    }
  ];

  return (
    <section id="blog" className="py-16 md:py-24 bg-white" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 section-heading">
            Milk Knowledge
          </h2>
          <p className="font-body text-base md:text-lg text-[#6B6659] max-w-2xl mx-auto mt-6">
            Learn more about milk, health, and why choosing pure matters. Tap to read.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <Card 
              key={index} 
              className="blog-card border-0 cursor-pointer group"
              onClick={() => setSelectedBlog(post)}
              data-testid={`blog-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#2E5C38]/10 rounded-full flex items-center justify-center group-hover:bg-[#2E5C38]/20 transition-colors">
                    <post.icon className="w-5 h-5 text-[#2E5C38]" />
                  </div>
                  <span className="bg-[#E89F28]/10 text-[#E89F28] text-xs px-3 py-1 rounded-full font-body font-medium">
                    {post.category}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-4">
                  {post.title}
                </h3>
                <div className="flex items-center text-[#2E5C38] font-body font-medium text-sm group-hover:gap-2 transition-all">
                  <span>Read More</span>
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Blog Detail Modal */}
      <Dialog open={!!selectedBlog} onOpenChange={() => setSelectedBlog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7]">
          {selectedBlog && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#2E5C38]/10 rounded-full flex items-center justify-center">
                    <selectedBlog.icon className="w-5 h-5 text-[#2E5C38]" />
                  </div>
                  <span className="bg-[#E89F28]/10 text-[#E89F28] text-xs px-3 py-1 rounded-full font-body font-medium">
                    {selectedBlog.category}
                  </span>
                </div>
                <DialogTitle className="font-heading text-2xl md:text-3xl text-[#2E5C38]">
                  {selectedBlog.fullContent.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Paragraphs */}
                {selectedBlog.fullContent.paragraphs?.map((para, idx) => (
                  <p key={idx} className="font-body text-[#6B6659] leading-relaxed">
                    {para}
                  </p>
                ))}

                {/* Benefits list */}
                {selectedBlog.fullContent.benefits && (
                  <ul className="space-y-2 pl-4">
                    {selectedBlog.fullContent.benefits.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                        <CheckCircle2 className="w-5 h-5 text-[#2E5C38] flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Facts (A1 vs A2) */}
                {selectedBlog.fullContent.facts && (
                  <ul className="space-y-2 pl-4">
                    {selectedBlog.fullContent.facts.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                        <Leaf className="w-5 h-5 text-[#2E5C38] flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Key Point */}
                {selectedBlog.fullContent.keyPoint && (
                  <div className="bg-[#2E5C38]/5 rounded-xl p-4">
                    <p className="font-body text-[#1A1A1A] font-medium">
                      {selectedBlog.fullContent.keyPoint}
                    </p>
                  </div>
                )}

                {/* Tips */}
                {selectedBlog.fullContent.tips && (
                  <div>
                    <p className="font-body text-[#6B6659] mb-3">{selectedBlog.fullContent.tipsTitle}</p>
                    <ul className="space-y-2 pl-4">
                      {selectedBlog.fullContent.tips.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                          <CheckCircle2 className="w-5 h-5 text-[#E89F28] flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Dangers (Adulteration) */}
                {selectedBlog.fullContent.dangers && (
                  <ul className="space-y-2 pl-4">
                    {selectedBlog.fullContent.dangers.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 font-body text-[#6B6659]">
                        <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Impact */}
                {selectedBlog.fullContent.impact && (
                  <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                    <p className="font-body text-[#6B6659]">
                      {selectedBlog.fullContent.impact}
                    </p>
                  </div>
                )}

                {/* Solution */}
                {selectedBlog.fullContent.solution && (
                  <p className="font-body text-[#6B6659]">
                    {selectedBlog.fullContent.solution}
                  </p>
                )}

                {/* Conclusion */}
                {selectedBlog.fullContent.conclusion?.map((para, idx) => (
                  <p key={idx} className="font-body text-[#6B6659] leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-16 md:py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
              Get in Touch
            </h2>
            <p className="font-body text-lg text-[#6B6659] mb-8">
              Ready to enjoy fresh, pure milk delivered to your doorstep? Contact us on WhatsApp — it's the fastest way to reach us!
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#25D366]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#25D366]" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-[#1A1A1A] mb-1">Phone / WhatsApp</h3>
                  <p className="font-body text-[#6B6659]">+91 6261610040</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#2E5C38]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#2E5C38]" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-[#1A1A1A] mb-1">Office Address</h3>
                  <p className="font-body text-[#6B6659]">Housing Board Colony, Nardaha</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#8B5E3C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-6 h-6 text-[#8B5E3C]" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-[#1A1A1A] mb-1">Farm Location</h3>
                  <p className="font-body text-[#6B6659]">Village Chandkhuri</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E89F28]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#E89F28]" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-[#1A1A1A] mb-1">Delivery Timings</h3>
                  <p className="font-body text-[#6B6659]">Morning & Evening</p>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="pt-4 border-t border-[#E6E0D4]">
                <h3 className="font-body font-semibold text-[#1A1A1A] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a 
                    href={SOCIAL_LINKS.instagram.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white px-4 py-3 rounded-xl hover:opacity-90 transition-opacity"
                    data-testid="contact-instagram-link"
                  >
                    <Instagram className="w-5 h-5" />
                    <span className="font-body text-sm">{SOCIAL_LINKS.instagram.username}</span>
                  </a>
                  <a 
                    href={SOCIAL_LINKS.linkedin.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-[#0077B5] text-white px-4 py-3 rounded-xl hover:opacity-90 transition-opacity"
                    data-testid="contact-linkedin-link"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span className="font-body text-sm">{SOCIAL_LINKS.linkedin.name}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp CTA Card */}
          <div className="flex items-center justify-center">
            <div className="bg-[#FDFBF7] rounded-3xl p-8 md:p-12 text-center max-w-md shadow-lg">
              <div className="w-20 h-20 bg-[#25D366]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-10 h-10 text-[#25D366]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
                Contact Us on WhatsApp
              </h3>
              <p className="font-body text-[#6B6659] mb-8">
                The fastest way to reach us! Send a message and we'll respond quickly with all the information you need.
              </p>
              <a 
                href={WHATSAPP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
              >
                <button className="whatsapp-btn w-full justify-center">
                  <Phone className="w-5 h-5" />
                  Contact on WhatsApp
                </button>
              </a>
              <p className="font-body text-sm text-[#6B6659] mt-4">
                +91 6261610040
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Floating WhatsApp Button
const FloatingWhatsAppButton = () => {
  return (
    <a 
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#128C7E] hover:scale-110 transition-all duration-300 animate-pulse-soft"
      data-testid="floating-whatsapp-btn"
      aria-label="Contact on WhatsApp"
    >
      <Phone className="w-6 h-6" />
    </a>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#1A1A1A] py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <img 
                src={ASSETS.brandLogo} 
                alt="Doorstep Doodhwala" 
                className="h-14 w-auto object-contain bg-white rounded-lg p-2"
              />
            </div>
            <p className="font-body text-white/50 text-sm">
              Delivering fresh, pure milk directly from our farm to your home since 2019.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-body font-semibold text-white mb-4">Quick Links</h4>
            <div className="space-y-2">
              {["Home", "About Us", "Our Milk", "Contact"].map((link) => (
                <a 
                  key={link} 
                  href={`#${link.toLowerCase().replace(' ', '')}`}
                  className="block font-body text-white/50 hover:text-[#E89F28] transition-colors text-sm"
                >
                  {link}
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-body font-semibold text-white mb-4">Contact Us</h4>
            <div className="space-y-3">
              <p className="font-body text-white/50 text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" /> +91 6261610040
              </p>
              <p className="font-body text-white/50 text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Housing Board Colony, Nardaha
              </p>
              <p className="font-body text-white/50 text-sm flex items-center gap-2">
                <Leaf className="w-4 h-4" /> Farm: Village Chandkhuri
              </p>
            </div>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="font-body font-semibold text-white mb-4">Follow Us</h4>
            <div className="space-y-3">
              <a 
                href={SOCIAL_LINKS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/50 hover:text-[#E89F28] transition-colors"
                data-testid="footer-instagram-link"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] rounded-lg flex items-center justify-center">
                  <Instagram className="w-4 h-4 text-white" />
                </div>
                <span className="font-body text-sm">{SOCIAL_LINKS.instagram.username}</span>
              </a>
              <a 
                href={SOCIAL_LINKS.linkedin.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/50 hover:text-[#E89F28] transition-colors"
                data-testid="footer-linkedin-link"
              >
                <div className="w-8 h-8 bg-[#0077B5] rounded-lg flex items-center justify-center">
                  <Linkedin className="w-4 h-4 text-white" />
                </div>
                <span className="font-body text-sm">{SOCIAL_LINKS.linkedin.name}</span>
              </a>
            </div>
          </div>
        </div>

        <Separator className="bg-white/10 my-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Doorstep Doodhwala. All rights reserved.
          </p>
          <div className="flex items-center gap-2">
            <span className="font-body text-white/40 text-sm">Made with</span>
            <Heart className="w-4 h-4 text-[#E89F28]" />
            <span className="font-body text-white/40 text-sm">for healthy families</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component
const Home = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <HeroSection />
      <TrustStatsSection />
      <AboutSection />
      <ProductsSection />
      <QualityProcessSection />
      <TestimonialsSection />
      <BlogSection />
      <ContactSection />
      <Footer />
      <FloatingWhatsAppButton />
    </div>
  );
};

// App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
